import { LlmUsageRecord } from '../../../../services/llmUsage';

// Pure aggregators for the admin Token Dashboard. Inputs are llm_usage rows
// already shaped by `services/llmUsage.ts`; outputs are summary structures the
// view renders as numbers and bar charts. Kept React-free + Firestore-free so
// tests can drive a fixed dataset without rendering or mocking the SDK.

export interface UsageTotals {
  callCount: number;
  errorCount: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  totalTokens: number;
  costMicroUsd: number;
}

export interface CampaignUsage extends UsageTotals {
  campaignId: string;
  campaignName: string;
  /** Cost per day in micro-USD, aligned with the global `perDay[].dateKey` order. */
  dailyCost: number[];
}

export interface ModelUsage extends UsageTotals {
  model: string;
  provider: string;
  /** Cost per day in micro-USD, aligned with the global `perDay[].dateKey` order. */
  dailyCost: number[];
}

export interface DailyUsage extends UsageTotals {
  /** Local-time YYYY-MM-DD key. */
  dateKey: string;
}

export interface UsageSummary {
  totals: UsageTotals;
  perCampaign: CampaignUsage[];
  perModel: ModelUsage[];
  perDay: DailyUsage[];
  /** Earliest/latest createdAt in the dataset, or null when empty. */
  earliest: Date | null;
  latest: Date | null;
}

function emptyTotals(): UsageTotals {
  return {
    callCount: 0,
    errorCount: 0,
    inputTokens: 0,
    outputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
    totalTokens: 0,
    costMicroUsd: 0,
  };
}

function accumulate(into: UsageTotals, rec: LlmUsageRecord) {
  into.callCount += 1;
  // Writers stamp `'done'` on success and `'error'` on failure (see
  // functions/services/llmStream.js + llmReviewer.js). Only count the
  // explicit error state — anything else (including the legacy empty
  // status from older rows) is treated as a non-error call.
  if (rec.status === 'error') into.errorCount += 1;
  into.inputTokens += rec.usage.inputTokens;
  into.outputTokens += rec.usage.outputTokens;
  into.cacheReadTokens += rec.usage.cacheReadTokens;
  into.cacheWriteTokens += rec.usage.cacheWriteTokens;
  into.totalTokens +=
    rec.usage.inputTokens +
    rec.usage.outputTokens +
    rec.usage.cacheReadTokens +
    rec.usage.cacheWriteTokens;
  into.costMicroUsd += rec.costMicroUsd;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Aggregate a flat list of usage records into the dashboard's display shape. */
export function aggregateUsage(records: LlmUsageRecord[]): UsageSummary {
  const totals = emptyTotals();
  const perCampaign = new Map<string, CampaignUsage>();
  const perModel = new Map<string, ModelUsage>();
  const perDay = new Map<string, DailyUsage>();
  // Per-group, per-day cost in micro-USD. Resolved to aligned arrays after we
  // know the canonical chronological `perDay` order — this keeps sparklines on
  // a shared x-axis without each row having to discover the date list itself.
  const campaignDaily = new Map<string, Map<string, number>>();
  const modelDaily = new Map<string, Map<string, number>>();
  let earliest: Date | null = null;
  let latest: Date | null = null;

  for (const rec of records) {
    accumulate(totals, rec);

    const campaignId = rec.campaignId ?? '__none__';
    const campaignName =
      rec.campaignName ?? (rec.campaignId ? rec.campaignId : '(no campaign)');
    let camp = perCampaign.get(campaignId);
    if (!camp) {
      camp = { campaignId, campaignName, dailyCost: [], ...emptyTotals() };
      perCampaign.set(campaignId, camp);
    } else if (rec.campaignName && camp.campaignName !== rec.campaignName) {
      // Prefer the most recent name we've seen so renames propagate.
      camp.campaignName = rec.campaignName;
    }
    accumulate(camp, rec);

    const modelKey = rec.model || 'unknown';
    let mdl = perModel.get(modelKey);
    if (!mdl) {
      mdl = {
        model: modelKey,
        provider: rec.provider || 'unknown',
        dailyCost: [],
        ...emptyTotals(),
      };
      perModel.set(modelKey, mdl);
    }
    accumulate(mdl, rec);

    if (rec.createdAt) {
      if (!earliest || rec.createdAt < earliest) earliest = rec.createdAt;
      if (!latest || rec.createdAt > latest) latest = rec.createdAt;

      const dk = dateKey(rec.createdAt);
      let day = perDay.get(dk);
      if (!day) {
        day = { dateKey: dk, ...emptyTotals() };
        perDay.set(dk, day);
      }
      accumulate(day, rec);

      let campDays = campaignDaily.get(campaignId);
      if (!campDays) {
        campDays = new Map<string, number>();
        campaignDaily.set(campaignId, campDays);
      }
      campDays.set(dk, (campDays.get(dk) ?? 0) + rec.costMicroUsd);

      let modelDays = modelDaily.get(modelKey);
      if (!modelDays) {
        modelDays = new Map<string, number>();
        modelDaily.set(modelKey, modelDays);
      }
      modelDays.set(dk, (modelDays.get(dk) ?? 0) + rec.costMicroUsd);
    }
  }

  // Fill gap days between earliest and latest so the time axis is geometric,
  // not just ordinal — silently dropping zero-cost days would let a 13-day
  // gap render the same width as a 1-day gap (Tufte: show data variation,
  // not design variation).
  if (earliest && latest) {
    const cursor = new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
    const end = new Date(latest.getFullYear(), latest.getMonth(), latest.getDate());
    while (cursor <= end) {
      const dk = dateKey(cursor);
      if (!perDay.has(dk)) {
        perDay.set(dk, { dateKey: dk, ...emptyTotals() });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  const orderedDays = Array.from(perDay.values()).sort((a, b) =>
    a.dateKey.localeCompare(b.dateKey),
  );
  const dateKeys = orderedDays.map((d) => d.dateKey);

  Array.from(perCampaign.values()).forEach((camp) => {
    const days = campaignDaily.get(camp.campaignId);
    camp.dailyCost = dateKeys.map((k) => days?.get(k) ?? 0);
  });
  Array.from(perModel.values()).forEach((mdl) => {
    const days = modelDaily.get(mdl.model);
    mdl.dailyCost = dateKeys.map((k) => days?.get(k) ?? 0);
  });

  return {
    totals,
    perCampaign: Array.from(perCampaign.values()).sort(
      (a, b) => b.costMicroUsd - a.costMicroUsd,
    ),
    perModel: Array.from(perModel.values()).sort(
      (a, b) => b.costMicroUsd - a.costMicroUsd,
    ),
    perDay: orderedDays,
    earliest,
    latest,
  };
}

/** Convenience: micro-USD → display string like "$12.34" or "$0.0021". */
export function formatUsd(micro: number): string {
  const usd = micro / 1_000_000;
  if (usd === 0) return '$0.00';
  if (Math.abs(usd) < 0.01) return `$${usd.toFixed(4)}`;
  if (Math.abs(usd) < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Compact integer formatter used in stat cards and chart axes. */
export function formatCompactNumber(n: number): string {
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}
